<script setup>
import { onUnmounted, ref } from 'vue'
import { useActiveAnnouncements } from '../composables/useActiveAnnouncements.js'

const { visibleMarqueeItems, dismiss } = useActiveAnnouncements()

const marqueeStates = ref({})
const marqueeObservers = new Map()
const marqueeRefCallbacks = new Map()

function measureMarquee(id, rootEl) {
  if (!rootEl) return
  const first = rootEl.querySelector('.announcement-marquee-text')
  if (!first) return

  const containerW = rootEl.clientWidth
  const textW = first.getBoundingClientRect().width
  const fromX = `${containerW}px`
  const toX = `-${textW}px`

  const prev = marqueeStates.value[id]
  if (prev && prev.fromX === fromX && prev.toX === toX) {
    return
  }

  marqueeStates.value = {
    ...marqueeStates.value,
    [id]: { fromX, toX },
  }
}

function getMarqueeRef(id) {
  if (marqueeRefCallbacks.has(id)) {
    return marqueeRefCallbacks.get(id)
  }

  const callback = (el) => {
    const prev = marqueeObservers.get(id)
    if (prev) {
      prev.disconnect()
      marqueeObservers.delete(id)
    }
    if (!el) return

    let rafId = 0
    const run = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = 0
        measureMarquee(id, el)
      })
    }

    run()
    const ro = new ResizeObserver(run)
    ro.observe(el)
    marqueeObservers.set(id, ro)
  }

  marqueeRefCallbacks.set(id, callback)
  return callback
}

function marqueeTrackStyle(id) {
  const state = marqueeStates.value[id]
  if (!state) return { visibility: 'hidden' }
  return {
    visibility: 'visible',
    '--marquee-from-x': state.fromX,
    '--marquee-to-x': state.toX,
  }
}

function dismissItem(id) {
  dismiss(id, { persist: true })
}

onUnmounted(() => {
  marqueeObservers.forEach((ro) => ro.disconnect())
  marqueeObservers.clear()
  marqueeRefCallbacks.clear()
})
</script>

<template>
  <div v-if="visibleMarqueeItems.length" class="announcement-stack">
    <div
      v-for="item in visibleMarqueeItems"
      :key="item.id"
      class="announcement-banner"
      :class="{ 'announcement-banner--clickable': item.dismissible }"
      role="status"
      aria-live="polite"
      :title="item.dismissible ? '点击关闭' : undefined"
      @click="item.dismissible && dismissItem(item.id)"
    >
      <div :ref="getMarqueeRef(item.id)" class="announcement-marquee" aria-hidden="true">
        <div
          class="announcement-marquee-track announcement-marquee-track--scroll"
          :style="marqueeTrackStyle(item.id)"
        >
          <span class="announcement-marquee-text">{{ item.content }}</span>
        </div>
      </div>
      <button
        v-if="item.dismissible"
        type="button"
        class="announcement-banner-close"
        aria-label="关闭公告"
        @click.stop="dismissItem(item.id)"
      >
        ×
      </button>
    </div>
  </div>
</template>

<style scoped>
.announcement-stack {
  flex-shrink: 0;
}

.announcement-banner {
  position: relative;
  overflow: hidden;
  background: #fef08a;
  color: #b54747;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.125rem;
  padding: 0.4375rem 2.5rem 0.4375rem 0;
}

.announcement-banner--clickable {
  cursor: pointer;
  user-select: none;
}

.announcement-marquee {
  overflow: hidden;
  width: 100%;
}

.announcement-marquee-track {
  display: flex;
  width: max-content;
}

.announcement-marquee-track--scroll {
  animation: announcement-marquee 24s linear infinite;
}

.announcement-marquee-text {
  flex-shrink: 0;
  white-space: nowrap;
}

.announcement-banner-close {
  position: absolute;
  top: 50%;
  right: 0.625rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border: none;
  border-radius: 9999px;
  background: rgba(181, 71, 71, 0.12);
  color: #b54747;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
}

.announcement-banner-close:hover {
  background: rgba(181, 71, 71, 0.2);
}

@keyframes announcement-marquee {
  0% {
    transform: translateX(var(--marquee-from-x));
  }
  100% {
    transform: translateX(var(--marquee-to-x));
  }
}
</style>
